import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography
} from "@mui/material";
import Grid from '@mui/material/Grid';
import {useLocation} from "react-router-dom";
import {useLazyQuery, useMutation} from "@apollo/client";
import React, {useEffect, useState} from "react";
import moment from "moment-timezone";

import CustomAlert from "./CustomAlert";
import {
    CREATE_SESSION,
    PUBLISHED_ACCREDITED_CONTENT_FOR_SHIFT,
    QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_AVAILABILITY_FILTERED_BY_TIME
} from "../queries/GraphQLQueries";
import {SESSION_TYPE} from "../domain/SessionType";
import ContentTypeSelect from "./ContentTypeSelect";
import ContentSelect from "./sessions/ContentSelect";
import {useDialogNavigation} from "../hooks/useDialogNavigation";

export default function CreateSessionDialog() {
    const handleClose = useDialogNavigation();
    const {state} = useLocation();
    const {shiftId, performer, sessionStartTime, serviceType} = state || {};
    const [accreditedContent, {data, loading}] = useLazyQuery(PUBLISHED_ACCREDITED_CONTENT_FOR_SHIFT, { fetchPolicy: 'network-only' });
    const [createSession, {}] = useMutation(CREATE_SESSION, {
        refetchQueries: [{query: QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_AVAILABILITY_FILTERED_BY_TIME}, 'GetTeachingUsersAndTheirAvailability']
    });
    const [selectedContentType, setSelectedContentType] = useState('');
    const [selectedContentIds, setSelectedContentIds] = useState([]);
    const [createEnabled, setCreateEnabled] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [selectedServiceTypeKeycode, setSelectedServiceTypeKeycode] = useState(serviceType.length === 1 ? serviceType[0].keycode : "");

    const availableContent = data?.availableContent || [];

    const groupedContent = Object.groupBy(
      availableContent,
      item => item.contentType || 'Other'
    );

    const allContentTypes = Object.keys(groupedContent).sort();

    useEffect(() => {
        accreditedContent({
            variables: {
                shiftId,
                serviceTypeKeycode: selectedServiceTypeKeycode
            }
        });
    }, [selectedServiceTypeKeycode, shiftId]);

    useEffect(() => {
        if (allContentTypes.length === 1) {
            setSelectedContentType(allContentTypes[0]);
        }
    }, [allContentTypes.length]);

    const filteredContent = groupedContent[selectedContentType] || [];

    function handleCloseCustomAlert() {
        setAlertMessage(null);
    }

    function handleContentChange(contentIds) {
        setSelectedContentIds(contentIds);
        setCreateEnabled(contentIds);
    }

    const handleServiceTypeKeycodeChange = (event) => {
        setSelectedContentIds([]);
        setSelectedContentType('');
        setCreateEnabled(false);
        setSelectedServiceTypeKeycode(event.target.value);
    }

    const handleContentTypeChange = (value) => {
        setSelectedContentType(value);
        setSelectedContentIds([]);
        setCreateEnabled(false);
    };

    const handleCreate = () => {
        setAlertMessage({ children: 'Creating Session...', severity: 'success' });
        setCreateEnabled(false);

        createSession({
            variables: {
                shiftId: shiftId,
                startTime: moment.utc(sessionStartTime),
                contentIds: selectedContentIds,
                sessionType: SESSION_TYPE.TEACHING
            }
        })
        .then(() => handleClose())
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            setCreateEnabled(true);
            console.error(error);
        });
    }

    return (
        <>
            <Dialog open={true} fullWidth onClose={handleClose}>
                <DialogTitle>Create Session</DialogTitle>

                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid size={6}>
                            <InputLabel>Service Type</InputLabel>
                            <FormControl fullWidth variant="standard">
                                <Select
                                    labelId="service-type-label"
                                    label="Service Type"
                                    displayEmpty
                                    value={selectedServiceTypeKeycode}
                                    onChange={handleServiceTypeKeycodeChange}>
                                    <MenuItem value="" disabled>Select Service Type</MenuItem>
                                    {serviceType.map((serviceType, index) => (
                                        <MenuItem key={index} value={serviceType.keycode}>{serviceType.description}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={6}>
                            <InputLabel id="performer-label">Performer</InputLabel>
                            <Typography>{performer}</Typography>
                        </Grid>
                        <Grid size={12}>
                            <InputLabel id="timeslot-label">Session Start Time</InputLabel>
                            <Typography>{moment(sessionStartTime).format('dddd MMM Do , h:mm a')}</Typography>
                        </Grid>
                        <Grid size={12}>
                            <ContentTypeSelect
                              handleContentTypeChange={handleContentTypeChange}
                              currentContentType={selectedContentType}
                              allContentTypes={allContentTypes}
                              disabled={!selectedServiceTypeKeycode} />
                        </Grid>

                        <Grid size={12} sx={{height: '100%'}}>
                            <ContentSelect
                                selectedContentIds={selectedContentIds}
                                handleContentChange={handleContentChange}
                                availableContent={availableContent}
                                loading={loading}
                                filteredContent={filteredContent}
                                disabled={!selectedContentType} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="contained">Cancel</Button>
                    <Button onClick={handleCreate} disabled={!createEnabled} variant="contained">Create</Button>
                </DialogActions>
            </Dialog>
            <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
        </>
    );
}